// Shared helpers and UI pieces for the Original Games admin pages.
// Styling follows components/History.tsx so the section looks like the rest of the panel.

import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, ChevronLeft, ChevronRight, Dices } from "lucide-react";
import { apiFetch } from "../../utils/api";
import { Num, Pagination, PagedResponse } from "./types";

export const API = "/lords/originalgames";

/* ── formatting ─────────────────────────────────────────────────── */
export const fmtAmt = (v: Num | null | undefined, max = 8): string => {
  const n = Number(v);
  if (v === null || v === undefined || !isFinite(n)) return "—";
  return n.toLocaleString("en-US", { maximumFractionDigits: max });
};
export const fmtInt = (v: Num | null | undefined): string => (v === null || v === undefined ? "—" : Number(v).toLocaleString());
export const fmtDate = (d: string | null | undefined): string => (d ? new Date(d).toLocaleString("en-GB", {
  year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
}) : "—");
export const short = (s: string | null | undefined, n = 10): string => (s ? (s.length > n ? s.slice(0, n) + "…" : s) : "—");
export const isoDay = (d: Date): string => d.toISOString().slice(0, 10);
export const daysAgo = (n: number): string => isoDay(new Date(Date.now() - n * 86400000));
export const errMsg = (e: unknown): string => (e instanceof Error ? e.message : String(e));

type QueryValue = string | number | boolean | null | undefined;
export const qs = (o: Record<string, QueryValue>): string => {
  const p = new URLSearchParams();
  Object.keys(o).forEach((k) => {
    const v = o[k];
    if (v !== "" && v !== null && v !== undefined && v !== false) p.set(k, v === true ? "1" : String(v));
  });
  const s = p.toString();
  return s ? "?" + s : "";
};

export const KIND_COLORS: Record<string, string> = {
  BET: "bg-[#2B6EF5]/15 text-[#4A85F7]",
  WIN: "bg-[#0ECC68]/15 text-[#0ECC68]",
  SETTLE_BET: "bg-[#2B6EF5]/15 text-[#4A85F7]",
  SETTLE_WIN: "bg-[#0ECC68]/15 text-[#0ECC68]",
  ROLLBACK: "bg-[#FFC23F]/15 text-[#FFC23F]",
  BET_REFUSED: "bg-[#E01B4F]/15 text-[#E01B4F]",
};
export const STATUS_COLORS: Record<string, string> = {
  applied: "text-[#0ECC68]", noop: "text-[#878AA2]", refused: "text-[#E01B4F]",
  accepted: "text-[#0ECC68]", rejected: "text-[#E01B4F]",
  passed: "text-[#0ECC68]", failed: "text-[#E01B4F]", open: "text-[#5581F7]", closed: "text-[#878AA2]",
};
export const pnlColor = (v: Num): string => (Number(v) >= 0 ? "text-[#0ECC68]" : "text-[#E01B4F]");

/* ── class names ────────────────────────────────────────────────── */
export const inputCls = "bg-[#10182E] border border-[#1E2D55] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#2B6EF5] text-[#F9F9F9] placeholder-[#878AA2]";
export const btnCls = "flex items-center gap-2 px-4 py-2 bg-[#0E1831] hover:bg-[#162140] border border-[#1E2D55] rounded-lg text-sm transition-all";
export const thCls = "px-4 py-3 text-left font-medium whitespace-nowrap";
export const thR = thCls + " text-right";
export const tdCls = "px-4 py-3 whitespace-nowrap";
export const tdR = tdCls + " text-right font-mono";
export const theadRow = "text-[#878AA2] text-xs uppercase tracking-wider bg-[#0C0D1D]";
export const tbodyRow = "border-t border-[#1E2D55] hover:bg-[#162140]/40";
export const pageCls = "p-4 md:p-6 min-h-screen text-[#F9F9F9] space-y-6";
export const chipCls = "px-2 py-1 rounded bg-[#0E1831] border border-[#1E2D55]";

/* ── components ─────────────────────────────────────────────────── */
export function Badge({ text, map }: { text: string; map: Record<string, string> }) {
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded ${map[text] || "bg-[#1E2D55] text-[#878AA2]"}`}>{text}</span>;
}

interface HeaderProps { title: string; subtitle: string; onRefresh: () => void; loading: boolean; children?: React.ReactNode }
export function Header({ title, subtitle, onRefresh, loading, children }: HeaderProps) {
  return (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Dices size={22} className="text-[#4A85F7]" />{title}</h1>
        <p className="text-[#878AA2] text-sm mt-1">{subtitle}</p>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {children}
        <button onClick={onRefresh} className={btnCls}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
    </div>
  );
}

interface DateRangeProps { from: string; to: string; setFrom: (s: string) => void; setTo: (s: string) => void }
export function DateRange({ from, to, setFrom, setTo }: DateRangeProps) {
  return (
    <>
      <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className={inputCls} />
      <span className="text-[#878AA2] text-sm">to</span>
      <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className={inputCls} />
    </>
  );
}

export function TestToggle({ value, onChange }: { value: boolean; onChange: (b: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm text-[#878AA2] cursor-pointer select-none" title="Include the provider's validation / sandbox test players">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="accent-[#2B6EF5]" />
      Include test players
    </label>
  );
}

export function Pager({ p, onPage }: { p: Pagination; onPage: (n: number) => void }) {
  const btn = "flex items-center gap-1 px-3 py-1.5 bg-[#162140] hover:bg-[#1E2D55] rounded-lg text-xs disabled:opacity-40";
  return (
    <div className="flex items-center justify-between p-4 border-t border-[#1E2D55] text-sm">
      <span className="text-[#878AA2]">Page {p.currentPage} of {p.totalPages} · {fmtInt(p.totalCount)} rows</span>
      <div className="flex gap-2">
        <button disabled={p.currentPage <= 1} onClick={() => onPage(p.currentPage - 1)} className={btn}><ChevronLeft size={14} />Prev</button>
        <button disabled={p.currentPage >= p.totalPages} onClick={() => onPage(p.currentPage + 1)} className={btn}>Next<ChevronRight size={14} /></button>
      </div>
    </div>
  );
}

export function Panel({ title, right, children }: { title: React.ReactNode; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-[#0E1831] rounded-xl border border-[#1E2D55] overflow-hidden">
      <div className="p-4 border-b border-[#1E2D55] flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <h2 className="text-lg font-semibold">{title}</h2>
        {right && <div className="flex flex-wrap gap-2 items-center">{right}</div>}
      </div>
      {children}
    </div>
  );
}

export function FilterBar({ children }: { children: React.ReactNode }) {
  return <div className="p-4 border-b border-[#1E2D55] flex flex-wrap gap-3 items-center">{children}</div>;
}

export function ErrorBox({ error }: { error: string | null }) {
  if (!error) return null;
  let msg = error;
  try {
    const j = JSON.parse(error) as { error?: string; message?: string };
    msg = j.error || j.message || error;
  } catch (_) { /* plain-text error */ }
  return <div className="rounded-lg border border-[#E01B4F]/40 bg-[#E01B4F]/10 text-[#E01B4F] px-4 py-3 text-sm">{msg}</div>;
}

export function EmptyRow({ cols, text }: { cols: number; text: string }) {
  return <tr><td colSpan={cols} className="text-center py-10 text-[#878AA2]">{text}</td></tr>;
}

export function PlayerCell({ name, id }: { name: string | null; id: Num }) {
  return <td className={tdCls}><div className="font-medium">{name || "—"}</div><div className="text-xs text-[#878AA2] font-mono">{id}</div></td>;
}

/* ── hooks ──────────────────────────────────────────────────────── */
const EMPTY_PAGE: Pagination = { totalCount: 0, totalPages: 1, currentPage: 1, limit: 20 };

// Server-paginated list. Changing any filter jumps back to page 1.
export function usePaged<T>(path: string, pick: (d: PagedResponse) => T[] | undefined, filters: Record<string, QueryValue>) {
  const [rows, setRows] = useState<T[]>([]);
  const [pagination, setPagination] = useState<Pagination>(EMPTY_PAGE);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = JSON.stringify(filters);

  useEffect(() => { setPage(1); }, [key]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<PagedResponse>(API + path + qs({ ...JSON.parse(key), page, limit: 20 }));
      setRows(pick(data) || []);
      setPagination(data.pagination || EMPTY_PAGE);
    } catch (e) {
      setError(errMsg(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
    // `pick` is a stable accessor per page; `key` stands in for `filters`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key, page]);

  useEffect(() => { load(); }, [load]);
  return { rows, pagination, setPage, loading, error, load };
}

// Debounce a text input so typing does not fire a request per keystroke.
export function useDebounced<T>(value: T, ms = 400): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
